import { SAFE_ZONE_REGISTRY } from '@src/safe-zones/registry';
import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import { SAFE_ZONE_Z_INDEX } from '@src/safe-zones/zIndex';
import { VARIANT_ORDER } from '@src/safe-zones/types';
import InfoIconAlt from '@src/svgs/InfoIconAlt';
import { observer } from 'mobx-react-lite';
import { useId } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { SafeZoneHintTooltipContent } from '../stage/safe-zones/SafeZoneHintTooltip';

import type {
  SafeZoneSelection,
  SafeZoneVariantHint,
  SafeZoneVariantKey,
} from '@src/safe-zones/types';

// react-tooltip's default chrome (background, padding, shadow) clashes with
// the rich hint body we render via `SafeZoneHintTooltipContent`. Strip it so
// the body controls its own visual treatment, and lift the tooltip above the
// share modal stack.
const PILL_TOOLTIP_RESET: import('react').CSSProperties = {
  background: 'transparent',
  padding: 0,
  border: 'none',
  boxShadow: 'none',
  zIndex: SAFE_ZONE_Z_INDEX.toolbarTooltip,
  opacity: 1,
};

interface Props {
  selection: SafeZoneSelection;
  /** Read each variant's current boolean state from the session store. */
  isActive: (key: SafeZoneVariantKey) => boolean;
  /** User-driven flip — fires analytics at the call site. */
  onToggle: (key: SafeZoneVariantKey) => void;
}

/**
 * Renders one pill per declared variant on the active overlay. Hidden when
 * no overlay is selected or when the active definition declares no variants
 * — keeps the toolbar quiet in the default state.
 */
const SafeZoneVariantToggles = observer(function SafeZoneVariantToggles({
  selection,
  isActive,
  onToggle,
}: Props) {
  if (selection === 'none') return null;
  const definition = SAFE_ZONE_REGISTRY[selection];
  if (!definition?.variants?.length) return null;

  const declared = new Set<SafeZoneVariantKey>(definition.variants);

  return (
    <Cluster data-testid="safe-zone-variant-toggles">
      {VARIANT_ORDER.filter(key => declared.has(key)).map(key => (
        <VariantPill
          key={key}
          variantKey={key}
          active={isActive(key)}
          hint={definition.variantHints?.[key]}
          onToggle={onToggle}
        />
      ))}
    </Cluster>
  );
});

export default SafeZoneVariantToggles;

interface VariantPillProps {
  variantKey: SafeZoneVariantKey;
  active: boolean;
  hint?: SafeZoneVariantHint;
  onToggle: (key: SafeZoneVariantKey) => void;
}

/**
 * Info indicator + On/Off pill. The hover tooltip is anchored to the **info
 * icon** rather than the entire pill so the toolbar doesn't pop hint cards
 * every time the cursor brushes a toggle; creators only summon the
 * description when they actively look for it. Clicking the pill flips the
 * variant; clicking / hovering the info icon reveals the hint without
 * flipping anything.
 *
 * The info glyph mirrors the small `(i)` indicator used elsewhere in the
 * product (e.g. prompt themes) — same shared `InfoIconAlt`.
 *
 * When the registry omits a hint for this variant, the info icon is
 * suppressed.
 */
function VariantPill({ variantKey, active, hint, onToggle }: VariantPillProps) {
  const tooltipId = useId();
  const labels = SAFE_ZONE_STRINGS.variants[variantKey];
  const hintAriaLabel = hint ? `About ${hint.title}` : `About ${variantKey}`;
  return (
    <PillGroup>
      {hint && (
        <HintInfo
          type="button"
          data-tooltip-id={tooltipId}
          data-testid={`safe-zone-variant-${variantKey}-info`}
          aria-label={hintAriaLabel}
        >
          <InfoIconAlt width="11" height="11" />
        </HintInfo>
      )}
      <Pill
        $active={active}
        onClick={() => onToggle(variantKey)}
        data-testid={`safe-zone-variant-${variantKey}`}
        aria-pressed={active}
      >
        <PillLabel $visible={active} aria-hidden={!active}>
          {labels.on}
        </PillLabel>
        <PillLabel $visible={!active} aria-hidden={active}>
          {labels.off}
        </PillLabel>
      </Pill>
      {hint && (
        <Tooltip
          id={tooltipId}
          style={PILL_TOOLTIP_RESET}
          place="bottom"
          positionStrategy="fixed"
          opacity={1}
          noArrow
          // Keep the tooltip open while the cursor is inside its body so
          // creators can mouse into the panel and select / copy text. The
          // tooltip closes only when the cursor leaves both the info icon
          // and the tooltip body.
          clickable
          render={() => (
            <SafeZoneHintTooltipContent
              title={hint.title}
              description={hint.description}
              proTips={hint.proTips}
            />
          )}
        />
      )}
    </PillGroup>
  );
}

const Cluster = styled.div`
  display: flex;
  align-items: center;
  /* Wider gap between (info + pill) units than inside a unit (PillGroup's own
     gap is 3px) so each info icon reads as belonging to its pill rather than
     floating between two of them. */
  gap: 10px;
`;

// Per-variant unit — keeps the info icon glued to its pill so the icon
// reads as that variant's affordance even when multiple pills sit in the
// cluster.
const PillGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
`;

// Small info indicator that anchors the variant's hint tooltip. Mirrors
// the compact `(i)` glyph used in prompt themes — sized to sit inline next
// to the pill without dominating the toolbar. Hover lifts the icon color
// from muted to foreground; the cursor flips to `help` so it's clear this
// isn't another toggle.
const HintInfo = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 50%;
  color: var(--text-muted);
  cursor: help;
  transition:
    color 150ms ease-out,
    background-color 150ms ease-out;

  &:hover,
  &:focus-visible {
    color: var(--foreground);
    background: rgba(255, 255, 255, 0.06);
    outline: none;
  }
`;

// Matches the DRAFT status badge in ClipDimensionsDropdown so the toolbar
// reads as one consistent row. Active state uses --accent (gold) since
// --primary (green) is reserved for primary actions.
//
// `inline-grid` with both labels in the same `1/1` cell holds the pill at
// max(onLabel, offLabel) intrinsic width — the inactive label stays in the
// layout (just visibility-hidden) so flipping state never reflows siblings.
const Pill = styled.button<{ $active: boolean }>`
  box-sizing: border-box;
  display: inline-grid;
  place-items: center;
  height: 16px;
  padding: 0 6px;
  border: none;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 150ms ease-out,
    color 150ms ease-out;

  background: ${({ $active }) => ($active ? 'var(--accent)' : 'transparent')};
  color: ${({ $active }) =>
    $active ? 'var(--background)' : 'var(--text-secondary)'};

  &:hover {
    background: ${({ $active }) =>
      $active ? 'var(--accent)' : 'rgba(255, 255, 255, 0.06)'};
    color: ${({ $active }) =>
      $active ? 'var(--background)' : 'var(--foreground)'};
  }
`;

const PillLabel = styled.span<{ $visible: boolean }>`
  grid-area: 1 / 1;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`;
